/**
 * Scheme allowlist for URLs that get written into src, href or poster attributes.
 * Anything outside it - javascript:, data:, vbscript: and friends - is a script execution
 * or content injection vector.
 */
var ALLOWED_PROTOCOLS = ['http:', 'https:'];

/**
 * Removes the characters a browser silently ignores while resolving a URL.
 * Without this, 'java\tscript:alert(1)' reads as a relative path to a scheme check but still
 * executes once the browser has stripped the tab.
 *
 * @param {string} url - raw URL
 * @returns {string} URL without surrounding whitespace or embedded control characters
 */
function stripIgnoredCharacters(url) {
    // eslint-disable-next-line no-control-regex
    return url.replace(/[\u0000-\u001F\u007F]/g, '').trim();
}

/**
 * Reads the scheme of a URL without resolving it.
 *
 * @param {string} url - URL to inspect
 * @returns {string} lower-cased scheme including the colon, or an empty string when there is none
 */
function getScheme(url) {
    var match = url.match(/^([a-zA-Z][a-zA-Z\d+\-.]*):/);

    return match ? match[1].toLowerCase() + ':' : '';
}

/**
 * @param  {string} str
 * Sanitized a string to replace iOS 'smart' characters with ones that downstream systems can handle
 * also removes emojis
 * @returns {string} filtered string
 */
export function sanitizeString(str) {
    // eslint-disable-next-line no-control-regex
    var regxpUnicode = new RegExp('[^\\p{L}\\p{N}\\p{P}\\p{Z}+^$\n]', 'gu');
    return (
        str
            .replace(/[\u2014]/g, '--') // emdash
            .replace(/[\u2022]/g, '*') // bullet
            .replace(/^\s+/g, '') // space characters at the beginning
            .replace(/[\u2018\u2019]/g, "'") // smart single quotes
            .replace(/[\u201C\u201D]/g, '"') // smart double quotes
            .replace(/…/g, '.') // smart ellipsis converted to a single dot (retain string value length)
            // StackOverflow source of regex to remove emojis https://stackoverflow.com/a/41543705
            // further refined with a regex for Unicode property escapes: https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Regular_expressions/Unicode_character_class_escape

            // whitelisted characters (+,=)
            .replace(regxpUnicode, '')
    );
}

/**
 * Returns a URL only when it is safe to assign to an attribute the browser will fetch
 *
 * @param {string} url - URL to check, typically read from a data attribute
 * @returns {string} the URL unchanged, or an empty string when it is not safe to use
 */
export function sanitizeUrl(url) {
    if (!url || typeof url !== 'string') {
        return '';
    }

    var cleaned = stripIgnoredCharacters(url);

    if (!cleaned) {
        return '';
    }

    // Parsing beats pattern matching for the absolute case, since the browser applies the same
    // rules it will use when it fetches the URL. Resolving against the page handles the
    // relative and protocol-relative forms for free.
    if (typeof window !== 'undefined' && typeof window.URL === 'function') {
        try {
            if (ALLOWED_PROTOCOLS.indexOf(new window.URL(cleaned, window.location.href).protocol) === -1) {
                return '';
            }

            return cleaned;
        } catch (e) {
            return '';
        }
    }

    var scheme = getScheme(cleaned);

    return !scheme || ALLOWED_PROTOCOLS.indexOf(scheme) !== -1 ? cleaned : '';
}
